<script setup lang="ts">
import type { NewsGalleryItem } from '~/composables/useNews'
const route = useRoute()
const { getNewsBySlug, getLatestNews } = useNews()
const { setArticleSeo } = useSeo()
const { data: article } = await useAsyncData(`news-${route.params.slug}`, () => getNewsBySlug(route.params.slug as string))
const { data: latestNews } = await useAsyncData('latest-news-sidebar', () => getLatestNews(3))

if (!article.value) {
  throw createError({ statusCode: 404, statusMessage: 'Article non trouvé' })
}

// Filter out current article from related
const relatedArticles = computed(() => 
  latestNews.value?.filter(n => n.slug !== route.params.slug).slice(0, 2) || []
)

// Set SEO using the composable
await setArticleSeo({
  title: article.value.title,
  excerpt: article.value.excerpt,
  metaTitle: article.value.metaTitle,
  metaDescription: article.value.metaDescription,
  keywords: article.value.keywords,
  ogImage: article.value.ogImage,
  image: article.value.image,
  noIndex: article.value.noIndex,
  date: article.value.date
})

const { siteUrl, setStructuredData, fetchSchoolInfo } = useStructuredData()
const { data: schoolInfo } = await useAsyncData('school-info-structured-data', fetchSchoolInfo)
setStructuredData(buildArticleSchema(siteUrl, {
  title: article.value.title,
  description: article.value.metaDescription || article.value.excerpt,
  dateRaw: article.value.dateRaw,
  image: article.value.ogImage || article.value.image,
  url: buildCanonicalUrl(siteUrl, route.path)
}, schoolInfo.value?.logo))

const shareOnTwitter = () => {
  const text = encodeURIComponent(article.value?.title || 'Article OXO')
  const url = encodeURIComponent(window.location.href)
  window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank')
}

const shareOnLinkedIn = () => {
  const url = encodeURIComponent(window.location.href)
  window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank')
}

const copyLink = async () => {
  await navigator.clipboard.writeText(window.location.href)
}

const getCategoryColor = (category: string) => {
  switch (category) {
    case 'Masterclass': return 'bg-purple-500'
    case 'Événement': return 'bg-oxo-red'
    default: return 'bg-oxo-yellow text-slate-900'
  }
}

const selectedItem = ref<NewsGalleryItem | null>(null)
const galleryMedia = computed(() => article.value?.gallery?.filter(item => item.type === 'image' || item.type === 'video') || [])
</script>

<template>
  <div v-if="article" class="bg-background text-foreground min-h-screen transition-colors duration-300">
    <!-- Hero with Full Image -->
    <UiPageHero 
      :image="article.image" 
      :title="article.title"
      height="screen"
      align="center"
    >
      <template #badge>
        <UiBadge variant="primary">
          {{ article.category }}
        </UiBadge>
      </template>

      <template #info>
        <div class="flex flex-wrap items-center justify-center gap-6">
          <span class="inline-flex items-center gap-2 text-white font-bold uppercase text-xs tracking-widest">
            <i class="far fa-calendar text-oxo-red" aria-hidden="true"></i>
            <time :datetime="article.date">{{ article.date }}</time>
          </span>
        </div>
      </template>

      <template #actions>
        <AppButton to="/actus" variant="outline" size="md">
          <i class="fas fa-arrow-left mr-2" aria-hidden="true"></i>
          Retour aux actualités
        </AppButton>
      </template>
    </UiPageHero>


    <!-- Article Content -->
    <section class="container mx-auto px-container-px py-section-py">
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-12 max-w-7xl mx-auto">
        
        <!-- Main Content -->
        <div class="lg:col-span-8">
          <article class="bg-oxo-card-bg rounded-3xl shadow-xl p-8 md:p-12 border border-oxo-border">
            
            <!-- Lead/Excerpt -->
            <p v-if="article.excerpt" class="text-xl md:text-2xl font-medium text-foreground leading-relaxed mb-10 pb-10 border-b border-oxo-border">
              {{ article.excerpt }}
            </p>
            
            <!-- Article Body -->
            <div class="prose prose-lg dark:prose-invert max-w-none article-content" v-html="article.content"></div>

            <!-- Gallery Section -->
            <section v-if="galleryMedia.length > 0" class="mt-16 pt-10 border-t border-oxo-border">
               <h3 class="font-display font-black text-2xl uppercase text-foreground mb-8">Galerie</h3>
               
               <ul class="grid grid-cols-1 md:grid-cols-2 gap-6 list-none p-0">
                 <li 
                   v-for="(item, idx) in galleryMedia" 
                   :key="idx"
                   class="group relative overflow-hidden rounded-2xl bg-oxo-card-bg/20 border border-oxo-border cursor-pointer aspect-video"
                   @click="selectedItem = item"
                 >
                   <NuxtImg loading="lazy"
                     :src="item.type === 'video' ? (article.image) : item.url"
                     :alt="article.title"
                     class="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                     sizes="50vw md:33vw"
                   />
                   
                   <div class="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col items-center justify-center gap-4 backdrop-blur-xs">
                     <div class="w-12 h-12 rounded-full bg-oxo-red flex items-center justify-center scale-50 group-hover:scale-100 transition-transform duration-500">
                       <i :class="['fas', item.type === 'video' ? 'fa-play' : 'fa-expand', 'text-white']" aria-hidden="true"></i>
                     </div>
                     <span class="text-[10px] font-black uppercase tracking-[0.2em] text-white">{{ item.type === 'video' ? 'Lire la vidéo' : 'Agrandir l\'image' }}</span>
                   </div>
                 </li>
               </ul>
            </section>
          </article>
          
          <!-- Share Section -->
          <aside class="mt-8 bg-oxo-card-bg rounded-2xl p-6 border border-oxo-border flex flex-col sm:flex-row items-center justify-between gap-4" aria-label="Partager cet article">
            <span class="text-foreground-dim font-medium">Partager cet article</span>
            <div class="flex gap-3">
              <button 
                @click="shareOnTwitter"
                class="w-12 h-12 rounded-full bg-background border border-oxo-border flex items-center justify-center text-foreground hover:bg-[#1DA1F2] hover:text-white transition-all duration-300 hover:scale-110"
                aria-label="Partager sur Twitter"
              >
                <i class="fab fa-x-twitter text-lg" aria-hidden="true"></i>
              </button>
              <button 
                @click="shareOnLinkedIn"
                class="w-12 h-12 rounded-full bg-background border border-oxo-border flex items-center justify-center text-foreground hover:bg-[#0A66C2] hover:text-white transition-all duration-300 hover:scale-110"
                aria-label="Partager sur LinkedIn"
              >
                <i class="fab fa-linkedin-in text-lg" aria-hidden="true"></i>
              </button>
              <button 
                @click="copyLink"
                class="w-12 h-12 rounded-full bg-background border border-oxo-border flex items-center justify-center text-foreground hover:bg-oxo-red hover:text-white transition-all duration-300 hover:scale-110"
                aria-label="Copier le lien"
              >
                <i class="fas fa-link text-lg" aria-hidden="true"></i>
              </button>
            </div>
          </aside>
        </div>
        
        <!-- Sidebar -->
        <aside class="lg:col-span-4">
          <div class="sticky top-8 space-y-8">
            
            <!-- Linked Events -->
            <section v-if="article.linkedEvents && article.linkedEvents.length > 0" class="bg-oxo-card-bg rounded-2xl p-6 border border-oxo-border" aria-label="Événements liés">
              <header>
                <h3 class="font-display font-bold text-lg text-foreground mb-6 flex items-center gap-2">
                  <i class="fas fa-calendar-alt text-oxo-red" aria-hidden="true"></i>
                  Événements liés
                </h3>
              </header>
              <ul class="space-y-4 list-none p-0">
                <li v-for="event in article.linkedEvents" :key="event.id">
                  <NuxtLink 
                    :to="`/actus/evenement/${event.slug}`"
                    class="group block p-4 rounded-xl bg-background border border-oxo-border hover:bg-oxo-card-bg/20 transition-colors"
                  >
                    <div class="flex items-center gap-2 mb-1">
                      <span class="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm border border-oxo-red text-oxo-red">{{ event.type }}</span>
                      <time class="text-oxo-yellow font-bold text-xs">{{ event.date }}</time>
                    </div>
                    <h4 class="font-bold text-foreground line-clamp-2 group-hover:text-oxo-red transition-colors">
                      {{ event.title }}
                    </h4>
                  </NuxtLink>
                </li>
              </ul>
            </section>

            <!-- Related Articles -->
            <section v-if="relatedArticles.length > 0" class="bg-oxo-card-bg rounded-2xl p-6 border border-oxo-border" aria-label="Articles similaires">
              <header>
                <h3 class="font-display font-bold text-lg text-foreground mb-6 flex items-center gap-2">
                  <i class="fas fa-newspaper text-oxo-red" aria-hidden="true"></i>
                  À lire aussi
                </h3>
              </header>
              
              <ul class="space-y-4 list-none p-0">
                <li v-for="related in relatedArticles" :key="related.id">
                  <NuxtLink 
                    :to="`/actus/${related.slug}`"
                    class="group block p-4 rounded-xl bg-background border border-oxo-border hover:bg-oxo-card-bg/20 transition-colors"
                  >
                    <span class="text-xs font-bold text-oxo-red uppercase tracking-wider">{{ related.category }}</span>
                    <h4 class="font-bold text-foreground mt-1 line-clamp-2 group-hover:text-oxo-red transition-colors">
                      {{ related.title }}
                    </h4>
                    <time class="text-xs text-foreground-dim mt-2 block">{{ related.date }}</time>
                  </NuxtLink>
                </li>
              </ul>
            </section>

            <!-- Quick Navigation -->
            <nav class="bg-linear-to-br from-oxo-red to-red-600 rounded-2xl p-6 text-white" aria-label="Découvrir OXO">
              <h3 class="font-display font-bold text-lg mb-4">Découvrir OXO</h3>
              <ul class="space-y-3 list-none p-0">
                <li>
                  <NuxtLink to="/formations" class="flex items-center gap-3 opacity-80 hover:opacity-100 transition-opacity">
                    <i class="fas fa-graduation-cap w-5" aria-hidden="true"></i>
                    Nos formations
                  </NuxtLink>
                </li>
                <li>
                  <NuxtLink to="/projets" class="flex items-center gap-3 opacity-80 hover:opacity-100 transition-opacity">
                    <i class="fas fa-gamepad w-5" aria-hidden="true"></i>
                    Projets étudiants
                  </NuxtLink>
                </li>
                <li>
                  <NuxtLink to="/candidature" class="flex items-center gap-3 opacity-80 hover:opacity-100 transition-opacity">
                    <i class="fas fa-paper-plane w-5" aria-hidden="true"></i>
                    Postuler
                  </NuxtLink>
                </li>
              </ul>
            </nav>
          </div>
        </aside>
      </div>
    </section>

    <!-- Modal Transition -->
    <Transition name="modal">
      <div 
        v-if="selectedItem" 
        class="fixed inset-0 z-50 flex items-center justify-center p-6 bg-background/95 backdrop-blur-xl"
        @click.self="selectedItem = null"
      >
        <button 
          @click="selectedItem = null"
          class="absolute top-8 right-8 w-14 h-14 rounded-full bg-white/5 text-white flex items-center justify-center hover:bg-oxo-red transition-all group z-60"
        >
          <i class="fas fa-times text-xl group-hover:rotate-90 transition-transform" aria-hidden="true"></i>
        </button>

        <div class="relative w-full max-w-6xl max-h-[85vh] flex items-center justify-center">
          <div v-if="selectedItem.type === 'video'" class="w-full aspect-video rounded-3xl overflow-hidden shadow-2xl border border-white/10">
            <UiVideoPlayer 
              :src="selectedItem.url" 
              :autoplay="true"
            />
          </div>

          <img 
            v-else 
            :src="selectedItem.url" 
            :alt="article.title"
            class="max-w-full max-h-[80vh] object-contain rounded-3xl shadow-2xl border border-white/10"
          />
        </div>
      </div>
    </Transition>

    <!-- CTA -->
    <AppCTA
      title="REJOIGNEZ"
      titleHighlight="L'AVENTURE"
      subtitle="Prêt à transformer votre passion en métier ? Nos inscriptions pour la session 2026 sont ouvertes."
      buttonText="DÉPOSER MON DOSSIER"
      buttonLink="/candidature"
    />
  </div>

</template>

<style>
@reference "../../assets/css/tailwind.css";

/* Enhanced Prose Styles */
.article-content h2 {
  @apply font-display font-black text-3xl mt-12 mb-6 text-slate-900 dark:text-white;
}
.article-content h3 {
  @apply font-display font-bold text-2xl mt-10 mb-4 text-slate-900 dark:text-white;
}
.article-content p {
  @apply mb-6 text-slate-600 dark:text-gray-300 leading-relaxed text-lg;
}
.article-content ul, .article-content ol {
  @apply mb-6 ml-6 text-slate-600 dark:text-gray-300;
}
.article-content li {
  @apply mb-3 leading-relaxed;
}
.article-content ul li {
  @apply list-disc;
}
.article-content ol li {
  @apply list-decimal;
}
.article-content strong {
  @apply text-slate-900 dark:text-white font-bold;
}
.article-content a {
  @apply text-oxo-red hover:underline;
}
.article-content blockquote {
  @apply border-l-4 border-oxo-yellow pl-6 py-2 my-8 italic text-slate-700 dark:text-gray-300 bg-slate-50 dark:bg-white/5 rounded-r-lg;
}
.article-content img {
  @apply rounded-2xl my-8 shadow-lg;
}
.article-content pre {
  @apply bg-slate-900 text-white p-6 rounded-xl overflow-x-auto my-8;
}
.article-content code {
  @apply bg-slate-100 dark:bg-white/10 px-2 py-1 rounded text-sm text-oxo-red;
}

.modal-enter-active, .modal-leave-active {
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
}

.modal-enter-from, .modal-leave-to {
  opacity: 0;
  backdrop-filter: blur(0px);
}

.modal-enter-from .max-w-6xl {
  transform: scale(0.9);
}
</style>
